<!--
  Render as soon as we can name the org the session is acting in; the dropdown
  only opens when there is somewhere else to go. Gating the whole component on
  "more than one navigable org" hid the current org entirely in environments
  where no membership is navigable (local dev: ROOT_DOMAIN=localhost vs
  `*.photoflow.vn` memberships), which is its own way of not telling the user
  which organization they are in.
-->
@if (currentOrganization || hasMultipleOrgs) {
    <ng-container *transloco="let t">
        <!-- Button -->
        <button
            mat-button
            class="max-md:min-h-11"
            [matMenuTriggerFor]="hasMultipleOrgs ? orgMenu : null"
            [disabled]="!hasMultipleOrgs"
            [attr.aria-label]="
                (hasMultipleOrgs
                    ? t('orgPicker.switchOrganization') || 'Switch organization'
                    : t('orgPicker.currentOrganization') ||
                      'Current organization') +
                (currentOrganization ? ' — ' + currentOrganization.orgName : '')
            "
        >
            <mat-icon
                [svgIcon]="'heroicons_outline:building-office-2'"
            ></mat-icon>
            @if (currentOrganization) {
                <span class="mx-1 hidden max-w-40 truncate md:inline">{{
                    currentOrganization.orgName
                }}</span>
            }
            @if (hasMultipleOrgs) {
                <mat-icon
                    class="icon-size-4"
                    [svgIcon]="'heroicons_outline:chevron-down'"
                ></mat-icon>
            }
        </button>

        <!-- Org menu -->
        <mat-menu [xPosition]="'before'" #orgMenu="matMenu">
            <!--
              Heading, not a command. `<button mat-menu-item disabled>` painted it
              in Material's DISABLED palette (light theme: zinc-400 — the same grey
              that means "you cannot click this"), which is unreadable against the
              white menu, and it announced a dead menuitem to screen readers. A plain
              label in the secondary text colour reads clearly and keeps the menu's
              only menuitems the real switch targets.
            -->
            <div
                class="text-secondary px-4 py-3 text-sm font-medium leading-none"
            >
                {{ t('orgPicker.switchOrganization') || 'Switch organization' }}
            </div>
            <div class="divider my-2 border-t"></div>

            @for (org of organizations; track trackByOrgId($index, org)) {
                <!-- All rows stay enabled: disabling the current org painted it
                     in Material's disabled palette (grey, unreadable against the
                     menu) — it looked broken, not "selected". The current org is
                     marked with a primary check + "Current" label instead, and
                     clicking it is a harmless re-select. -->
                <button
                    mat-menu-item
                    [class.font-medium]="isCurrent(org)"
                    (click)="switchTo(org)"
                >
                    <span class="flex w-full items-center">
                        @if (org.orgLogoUrl) {
                            <img
                                class="mr-3 h-5 w-5 shrink-0 rounded-sm object-cover"
                                [src]="org.orgLogoUrl | absoluteMediaUrl"
                                [alt]="'Logo for ' + org.orgName"
                            />
                        }
                        @if (!org.orgLogoUrl) {
                            <mat-icon
                                class="mr-3 shrink-0 icon-size-5"
                                [svgIcon]="
                                    'heroicons_outline:building-office-2'
                                "
                            ></mat-icon>
                        }
                        <!-- #3783: min-w-0 lets the name (not the "Current"
                             tag) give up space; the full name stays reachable
                             via the title attribute. -->
                        <span
                            class="min-w-0 flex-auto truncate"
                            [class.text-primary]="isCurrent(org)"
                            [attr.title]="org.orgName"
                            >{{ org.orgName }}</span
                        >
                        @if (isCurrent(org)) {
                            <span
                                class="ml-2 flex shrink-0 items-center gap-1 whitespace-nowrap text-primary"
                                data-testid="org-picker-current"
                            >
                                <mat-icon
                                    class="icon-size-4"
                                    [svgIcon]="'heroicons_outline:check'"
                                ></mat-icon>
                                <span class="text-xs">{{
                                    t('orgPicker.current') || 'Current'
                                }}</span>
                            </span>
                        }
                    </span>
                </button>
            }
        </mat-menu>
    </ng-container>
}
